import React, { forwardRef, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    useTheme,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Fade,
    Tooltip
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import WhatshotRoundedIcon from '@mui/icons-material/WhatshotRounded';
import { Tarea, PrioridadTarea } from '../../types';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { generateColor } from '../../utils/colorUtils';

interface TareaCardProps {
    tarea: Tarea;
    onCompletar: (id: string) => void;
    // Props injected by Draggable
    isDragging?: boolean;
    draggableProps?: any; // From hello-pangea/dnd
    dragHandleProps?: any; // From hello-pangea/dnd
    onUpdate?: (id: string, data: Partial<Tarea>) => Promise<void>;
    onEdit?: (tarea: Tarea) => void;
    onDelete?: (id: string) => void;
}

const TareaCard = forwardRef<HTMLDivElement, TareaCardProps>(({
    tarea,
    onCompletar,
    onUpdate,
    onEdit,
    onDelete,
    isDragging,
    draggableProps,
    dragHandleProps
}, ref) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const openMenu = Boolean(anchorEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (onEdit) onEdit(tarea);
    };

    const handleDelete = () => {
        handleMenuClose();
        if (onDelete) onDelete(tarea.id);
    };

    const getPriorityColor = (priority?: PrioridadTarea) => {
        switch (priority) {
            case 'alta': return theme.palette.error.main;
            case 'media': return theme.palette.warning.main;
            case 'baja': return theme.palette.success.main;
            default: return theme.palette.grey[400];
        }
    };

    // Derived logic
    const priorityColor = getPriorityColor(tarea.prioridad);
    const isCompleted = tarea.completada;

    // Determine background color
    let bgColor = 'background.paper';
    if (isCompleted) {
        bgColor = '#F0FDF4';
    } else if (tarea.esUrgente) {
        bgColor = '#FFEBEE'; // Light red for urgent tasks
    }

    return (
        <Card
            ref={ref}
            {...draggableProps}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            elevation={isDragging ? 8 : 0}
            sx={{
                borderRadius: 4, // 16px
                mb: 2,
                border: '1px solid',
                borderColor: isCompleted ? 'transparent' : (tarea.esUrgente ? 'error.light' : 'divider'),
                bgcolor: bgColor,
                boxShadow: isDragging
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    : (isHovered && !isCompleted ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none'),
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'visible',
                opacity: isCompleted ? 0.8 : 1,
                transform: isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
                ...draggableProps?.style
            }}
        >
            {/* Priority Strip */}
            {!isCompleted && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 12,
                        bottom: 12,
                        width: 4,
                        bgcolor: priorityColor,
                        borderTopRightRadius: 4,
                        borderBottomRightRadius: 4,
                    }}
                />
            )}

            <CardContent sx={{ p: '12px !important', '&:last-child': { pb: '12px !important' } }}>
                {/* Row 1: Checkbox + Title + Menu */}
                <Box display="flex" alignItems="flex-start" gap={1}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onCompletar(tarea.id);
                        }}
                        sx={{
                            p: 0,
                            mt: 0.25, // Align with text
                            color: isCompleted ? 'success.main' : 'text.disabled',
                            transition: 'all 0.2s',
                            '&:hover': {
                                color: isCompleted ? 'success.dark' : 'text.secondary',
                                transform: 'scale(1.1)'
                            }
                        }}
                        disableRipple
                    >
                        {isCompleted ? (
                            <CheckCircleOutlineRoundedIcon sx={{ fontSize: 20 }} />
                        ) : (
                            <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 20 }} />
                        )}
                    </IconButton>

                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1.3,
                                fontSize: '0.9rem',
                                color: isCompleted ? 'text.secondary' : 'text.primary',
                                textDecoration: isCompleted ? 'line-through' : 'none',
                                opacity: isCompleted ? 0.7 : 1,
                                transition: 'all 0.2s',
                                mb: 0.5
                            }}
                        >
                            {tarea.nombre}
                        </Typography>
                    </Box>

                    {/* Menu Trigger - Visible on Hover, Open, or Mobile */}
                    <Box sx={{
                        opacity: {
                            xs: 1, // Always visible on mobile
                            sm: (isHovered || openMenu) ? 1 : 0 // Hover behavior on desktop
                        },
                        transition: 'opacity 0.2s',
                        alignSelf: 'flex-start',
                        ml: -0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}>
                        {/* Drag Handle - Added on the right */}
                        <Box
                            {...dragHandleProps}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.disabled',
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                '&:hover': { color: 'text.secondary' },
                                transition: 'color 0.2s',
                                ml: 0.5,
                                order: 3 // Ensure it's on the right
                            }}
                        >
                            <DragIndicatorIcon sx={{ fontSize: 20 }} />
                        </Box>

                        {/* Urgent Toggle Button */}
                        {!isCompleted && onUpdate && (
                            <Tooltip title={tarea.esUrgente ? "Quitar urgente" : "Marcar urgente"}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdate(tarea.id, { esUrgente: !tarea.esUrgente });
                                    }}
                                    sx={{
                                        padding: 0.25,
                                        color: tarea.esUrgente ? 'error.main' : 'text.disabled',
                                        '&:hover': {
                                            color: tarea.esUrgente ? 'error.dark' : 'error.main',
                                            bgcolor: 'action.hover'
                                        },
                                    }}
                                >
                                    <WhatshotRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        <IconButton
                            size="small"
                            onClick={handleMenuClick}
                            sx={{
                                padding: 0.25,
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                            }}
                        >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Row 2: Metadata (Obra, Time, Priority) */}
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" pl={3.5} mt={0.5}>
                    {tarea.obraNombre && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                ...(() => {
                                    const { bg, text, border } = generateColor(tarea.obraNombre);
                                    return {
                                        bgcolor: bg,
                                        color: text,
                                        borderColor: border
                                    };
                                })(),
                                borderRadius: '4px',
                                px: 0.6,
                                py: 0.2,
                                maxWidth: '100%',
                                border: '1px solid',
                            }}
                        >
                            <BusinessCenterOutlinedIcon sx={{ fontSize: 10, color: 'inherit' }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: 'inherit',
                                    fontSize: '0.65rem',
                                    textTransform: 'uppercase',
                                    lineHeight: 1
                                }}
                                noWrap
                            >
                                {tarea.obraNombre}
                            </Typography>
                        </Box>
                    )}

                    {tarea.fechaProgramada && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                color: isCompleted ? 'text.disabled' : 'text.secondary',
                            }}
                        >
                            <ScheduleIcon sx={{ fontSize: 12 }} />
                            {onUpdate ? (
                                <MobileTimePicker
                                    value={new Date(tarea.fechaProgramada)}
                                    onChange={(newTime) => {
                                        if (newTime) {
                                            const original = tarea.fechaProgramada ? new Date(tarea.fechaProgramada) : new Date();
                                            if (newTime.getTime() !== original.getTime()) {
                                                const updatedDate = new Date(original);
                                                updatedDate.setHours(newTime.getHours());
                                                updatedDate.setMinutes(newTime.getMinutes());
                                                onUpdate(tarea.id, { fechaProgramada: updatedDate });
                                            }
                                        }
                                    }}
                                    slots={{
                                        textField: (params) => (
                                            <Typography
                                                component="div"
                                                onClick={params.onClick}
                                                ref={params.InputProps?.ref as any}
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    cursor: 'pointer',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                {new Date(tarea.fechaProgramada!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        )
                                    }}
                                />
                            ) : (
                                <Typography variant="caption" fontWeight="600" fontSize="0.7rem">
                                    {new Date(tarea.fechaProgramada).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {tarea.recurrente && (
                        <Tooltip title="Tarea recurrente" arrow>
                            <RepeatRoundedIcon sx={{ fontSize: 14, color: 'info.main' }} />
                        </Tooltip>
                    )}

                    {/* Priority Label */}
                    {!isCompleted && tarea.prioridad && (
                        <Typography
                            variant="caption"
                            ml="auto"
                            sx={{
                                color: priorityColor,
                                fontWeight: 'bold',
                                fontSize: '0.6rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5
                            }}
                        >
                            {tarea.prioridad}
                        </Typography>
                    )}
                </Box>
            </CardContent>
            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                TransitionComponent={Fade}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        borderRadius: 3,
                        minWidth: 140,
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            gap: 1.5,
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Eliminar</ListItemText>
                </MenuItem>
            </Menu>
        </Card>
    );
});

export default TareaCard;
